/* eslint-disable no-case-declarations */
import { useCallback } from 'react';
import { SerializedError } from '@reduxjs/toolkit';
import { defaultCountry } from 'common/utilities/PhoneHelpers';
import TelemetryProvider from 'common/utilities/TelemetryProvider';
import { Crypto } from 'cryptoSdk/src/Crypto';
import { isAfter } from 'date-fns';
import parsePhoneNumber from 'libphonenumber-js';
import { UpdateSubscriberCyberDataRequest, UpdateSubscriberProfileApiResponse } from 'mcsclient/models';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { useWebView } from 'store/webView/useWebView';
import { isMacOS } from 'store/webView/webViewHelpers';

import { encryptDataValue, encryptDataValues, fetchE2EEncryptionKeys } from '../encryption/encryptionRequestHelpers';
import { MonitoringDataType, MonitoringDataValue } from '../identityMonitoring.types';
import { isValueEmpty } from '../identityMonitoringHelpers';
import { scanAndGetBreachesAsync, updateSubscriberProfileAsync } from '../identityMonitoringSlice';

const DuplicateExceptionCode = '1149';

type DuplicateInfo = {
  type: MonitoringDataType;
  index?: number;
};

/**
 * Error that holds the type and index of the duplicate info
 */
export class DuplicateError extends Error {
  readonly duplicateInfo: DuplicateInfo;

  constructor(duplicateInfo: DuplicateInfo, error: SerializedError) {
    super(error.message);
    this.duplicateInfo = duplicateInfo;
  }
}

/**
 * Build add request payload
 *
 * @param dataValues
 * @param encrypter
 */
const buildAddPayload = async (dataValues: MonitoringDataValue[], encrypter?: Crypto): Promise<UpdateSubscriberCyberDataRequest> => {
  const draft: UpdateSubscriberCyberDataRequest = { isEncrypted: !!encrypter };

  for await (const dataValue of dataValues) {
    if (!isValueEmpty(dataValue)) {
      switch (dataValue.dataType) {
        case 'MedicalID':
          const encMedicalId = await encryptDataValue(dataValue.value, encrypter);
          draft.medicalIDs = [...(draft.medicalIDs ?? []), encMedicalId!];
          break;
        case 'DriverLicense':
          const [number, state] = await encryptDataValues(
            [dataValue.value.driverLicenseNumber, dataValue.value.driverLicenseState],
            encrypter
          );

          draft.driverLicense = {
            number, // TODO: mapping no longer needed after #36938601 is completed
            state,
          };
          break;
        case 'Iban':
          const encIbans = await encryptDataValue(dataValue.value, encrypter);
          draft.ibans = [...(draft.ibans ?? []), encIbans!];
          break;
        case 'OtherCard':
          const encOtherCard = await encryptDataValue(dataValue.value, encrypter);
          draft.otherCards = [...(draft.otherCards ?? []), encOtherCard!];
          break;
        case 'Passport':
          const encPassport = await encryptDataValue(dataValue.value.passportNumber, encrypter);
          draft.passportDetails = [...(draft.passportDetails ?? []), { passportNumber: encPassport }];
          break;
        case 'NationalId':
          const [id, nationalCountryCode] = await encryptDataValues(
            [dataValue.value.id, dataValue.value.nationalCountryCode],
            encrypter
          );
          draft.nationalIdDetails = [...(draft.nationalIdDetails ?? []), { id, nationalCountryCode }];
          break;
        case 'BankAccount':
          const [accountNumber, bankRouting] = await encryptDataValues(
            [dataValue.value.accountNumber, dataValue.value.routingNumber],
            encrypter
          );

          draft.accountDetails = {
            ...draft.accountDetails,
            bankAccounts: [
              ...(draft.accountDetails?.bankAccounts ?? []),
              {
                accountNumber: accountNumber!,
                bankRouting, // TODO: no longer need to map bankRouting -> routingNumber after #36938601 is completed
              },
            ],
          };
          break;
        case 'CreditCard':
          const encCCNumber = await encryptDataValue(dataValue.value.cardNumber, encrypter);
          draft.accountDetails = {
            ...draft.accountDetails,
            creditCardNumbers: [...(draft.accountDetails?.creditCardNumbers ?? []), encCCNumber!],
          };
          break;
        case 'DebitCard':
          const encDCNumber = await encryptDataValue(dataValue.value.cardNumber, encrypter);
          draft.accountDetails = {
            ...draft.accountDetails,
            debitCardNumbers: [...(draft.accountDetails?.debitCardNumbers ?? []), encDCNumber!],
          };
          break;
        case 'TelephoneNumber': {
          const parsed = parsePhoneNumber(dataValue.value.telephone, { defaultCountry });
          if (parsed) {
            draft.telephoneNumbers = [...(draft.telephoneNumbers ?? []), parsed.nationalNumber];
          }
          break;
        }
        case 'EmailAddress':
          draft.emailAddresses = [...(draft.emailAddresses ?? []), dataValue.value.email];
          break;
        default:
          TelemetryProvider.Instance.trackException(
            new Error(`useAddMonitoringData: ${dataValue.dataType} is not supported`),
            'AttemptToAddUnsupportedDataType'
          );
      }
    }
  }

  return draft;
};

type UseAddMonitoringDataType = () => {
  /**
   * Wrapper function for triggering a scan after performing an action ({@link updateAction})
   */
  triggerScanAfter: (updateAction: () => Promise<unknown>) => Promise<number | undefined>;
  /**
   * Function add monitoring data to subscriber profile
   */
  addMonitoringData: (dataValues: MonitoringDataValue[]) => Promise<UpdateSubscriberProfileApiResponse>;
};

/**
 * Helper hook for adding monitoring items
 */
export const useAddMonitoringData: UseAddMonitoringDataType = () => {
  const dispatch = useAppDispatch();
  const { csid } = useAppSelector((state) => state.identityMonitoring.userContext);
  const { webViewPlatform, isWebView } = useWebView();
  const isMacOsWebView = isWebView && isMacOS(webViewPlatform);

  const triggerScanAfter = useCallback(
    async (updateAction: () => Promise<unknown>) => {
      const updateTime = new Date();

      try {
        await updateAction();
      } catch (error) {
        // only throw error if unable to add monitored data
        return Promise.reject(error);
      }

      try {
        let breaches;
        if (csid) {
          breaches = await dispatch(scanAndGetBreachesAsync({ fullScan: true, csid })).unwrap();
        }
        // count breaches that are newer than the updateTime
        return breaches?.filter(({ createdDateTime }) => createdDateTime && isAfter(createdDateTime, updateTime)).length;
      } catch (error) {
        TelemetryProvider.Instance.trackUnknownException(error, 'ScanAndGetBreachesAPIRequestFailed');
        // If scan failed, ignore error, and just fallback to the backend periodic scanning job
        return undefined;
      }
    },
    [csid, dispatch]
  );

  const addMonitoringData = useCallback(
    async (dataValues: MonitoringDataValue[]) => {
      const { encrypter, cipherTextBlob } = await fetchE2EEncryptionKeys(isMacOsWebView);
      const cyberData = await buildAddPayload(dataValues, encrypter);

      return dispatch(updateSubscriberProfileAsync({ cyberData, e2ESubscriberKey: cipherTextBlob, csid }))
        .unwrap()
        .catch((error: SerializedError) => {
          if (error.code === DuplicateExceptionCode) {
            // Translate error to DuplicateError if error code is DuplicateExceptionCode
            const duplicateInfo: DuplicateInfo = JSON.parse(error.message ?? '');
            throw new DuplicateError(duplicateInfo, error);
          }
          throw error;
        });
    },
    [csid, dispatch, isMacOsWebView]
  );

  return { triggerScanAfter, addMonitoringData };
};
